import React from 'react';
import { Building2, Users, Calendar, MoreVertical } from 'lucide-react';
import { formatDate } from '../../../../../shared/utils/date';
import { Dropdown } from '@/shared/components/atoms/Dropdown/Dropdown';
import { DataTable, ColumnDef } from '../../../../../shared/components/molecules/Table/DataTable';

interface SuperOrganizationsTableProps {
  organizations: any[];
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  page: number;
  limit: number;
  sortBy: 'name' | 'created_at';
  sortOrder: 'asc' | 'desc';
  handleSort: (field: 'name' | 'created_at') => void;
  handleDeleteClick: (org: any) => void;
  isDeletingId: string | null;
  navigate: (path: string) => void;
}

export function SuperOrganizationsTable({
  organizations,
  isLoading,
  isFetching,
  isError,
  page,
  limit,
  sortBy,
  sortOrder,
  handleSort,
  handleDeleteClick,
  isDeletingId,
  navigate
}: SuperOrganizationsTableProps) {
  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'enterprise':
        return {
          bg: 'rgba(255, 193, 7, 0.12)',
          text: '#ffc107',
          border: '1px solid rgba(255, 193, 7, 0.25)'
        };
      case 'scale':
        return {
          bg: 'rgba(56, 189, 248, 0.12)',
          text: '#38bdf8',
          border: '1px solid rgba(56, 189, 248, 0.25)'
        };
      default: // freemium
        return {
          bg: 'rgba(148, 163, 184, 0.12)',
          text: '#94a3b8',
          border: '1px solid rgba(148, 163, 184, 0.25)'
        };
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      header: 'S.No',
      key: 'sno',
      width: '60px',
      render: (_, rowIndex) => (
        <span style={{ color: 'var(--admin-card-subtext)', fontWeight: 600 }}>
          {String((page - 1) * limit + rowIndex + 1).padStart(2, '0')}
        </span>
      )
    },
    {
      header: 'Organization',
      key: 'name',
      sortable: true,
      render: (org) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '0',
            backgroundColor: 'rgba(56, 189, 248, 0.08)',
            border: '1px solid var(--admin-card-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#38bdf8',
            flexShrink: 0
          }}>
            <Building2 size={16} />
          </div>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '13px', fontWeight: 700, color: 'var(--admin-card-text)' }}>
              {org.name}
            </div>
            <div style={{ color: 'var(--admin-card-subtext)', fontSize: '11px', fontWeight: 400, marginTop: '2px' }}>
              slug: <span style={{ fontFamily: 'monospace' }}>{org.slug}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Subscription Plan',
      key: 'subscription',
      render: (org) => {
        const planDetails = getPlanColor(org.subscription?.plan || 'freemium');
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '0',
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: planDetails.text,
            backgroundColor: planDetails.bg,
            border: planDetails.border
          }}>
            {org.subscription?.plan || 'freemium'}
          </span>
        );
      }
    },
    {
      header: 'Workspace Seats',
      key: 'members',
      render: (org) => {
        const limitSeats = org.subscription?.max_members === 999999 ? 'Unlimited' : `${org.subscription?.max_members || 1} Seats`;
        const activeCount = org.members?.length || 0;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--admin-card-text)' }}>
              <Users size={12} style={{ color: '#38bdf8' }} /> {activeCount} Active
            </span>
            <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)' }}>
              Limit: {limitSeats}
            </span>
          </div>
        );
      }
    },
    {
      header: 'Created Date',
      key: 'created_at',
      sortable: true,
      render: (org) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--admin-card-subtext)' }}>
          <Calendar size={12} />
          {org.created_at ? formatDate(org.created_at) : 'N/A'}
        </span>
      )
    },
    {
      header: 'Actions',
      key: 'actions',
      render: (org) => (
        <div style={{ textAlign: 'right' }}>
          {isDeletingId === org.id ? (
            <div style={{
              width: '30px', height: '30px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--admin-card-border)',
              borderRadius: '0',
              backgroundColor: 'rgba(255, 255, 255, 0.03)'
            }}>
              <div style={{ width: '20px', height: '10px', backgroundColor: 'rgba(255, 255, 255, 0.08)' }} className="animate-pulse" />
            </div>
          ) : (
            <Dropdown
              value=""
              align="right"
              trigger={
                <div
                  style={{
                    padding: '6px 8px',
                    borderRadius: '0',
                    border: '1px solid var(--admin-card-border)',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    color: 'var(--admin-card-subtext)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className="hover:text-white transition-colors"
                >
                  <MoreVertical size={16} />
                </div>
              }
              onChange={(val) => {
                if (val === 'view_members') {
                  navigate(`/admin/organizations/${org.id}/members`);
                } else if (val === 'manage_kb') {
                  navigate(`/admin/knowledge?orgId=${org.id}`);
                } else if (val === 'delete') {
                  handleDeleteClick(org);
                }
              }}
              options={[
                { value: 'view_members', label: `View Members (${org.members?.length || 0})` },
                { value: 'manage_kb', label: 'Manage Knowledge Base' },
                { value: 'delete', label: 'Delete Workspace', color: '#ef4444' }
              ]}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <DataTable
      theme="admin"
      columns={columns}
      data={organizations}
      isLoading={isLoading || isFetching}
      isError={isError}
      total={organizations.length} // Handled by parent
      page={1}
      limit={10}
      onPageChange={() => {}}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={(key) => handleSort(key as 'name' | 'created_at')}
      emptyMessage="No Workspaces Found"
      errorMessage="Failed to fetch organizations. Ensure you are authorized as a SuperAdmin."
    />
  );
}
